import React from 'react';
import styles from '../../../styles/DocumentManagement.module.css';
import { useSidebar } from '../../../contexts/SidebarContext';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import type { UserDetails } from '../../../types/User';

type AgentStats = {
  totalItems: number;
  completedItems: number;
  missingItems: number;
  expiredItems: number;
  completionRate: number;
};

interface AgentCompletionRowProps {
  agent: UserDetails;
  agentStats: AgentStats;
}

function getInitials(user: UserDetails): string {
  const first = (user.displayName ?? '').trim().charAt(0);
  const last = (user.lastName ?? '').trim().charAt(0);
  return (first + last).toUpperCase() || '?';
}

function getBarFillClass(rate: number, s: CSSModuleClasses): string {
  if (rate >= 90) return s.progressBarFillSuccess;
  if (rate >= 70) return s.progressBarFillWarning;
  return s.progressBarFillDanger;
}

function getProgressLabelColor(rate: number): string {
  if (rate >= 90) return '#16a34a';
  if (rate >= 70) return '#d97706';
  return '#ef4444';
}

type CSSModuleClasses = { [key: string]: string };

const AgentCompletionRow: React.FC<AgentCompletionRowProps> = ({ agent, agentStats }) => {
  const { setActiveItem } = useSidebar();
  const { setSelectedUser } = useDocumentContext();

  if (!agentStats) return null;

  const handleClick = () => {
    setSelectedUser(agent);
    setActiveItem('docs');
  };

  const rate = agentStats.completionRate;

  return (
    <button
      type="button"
      className={styles.agentCardEnhanced}
      onClick={handleClick}
      aria-label={`View documents for ${agent.displayName}`}
    >
      {/* Top row: avatar + name/email */}
      <div className={styles.agentCardTop}>
        <div className={styles.agentAvatar}>{getInitials(agent)}</div>
        <div className={styles.agentCardInfo}>
          <div className={styles.agentCardNameEnhanced}>{agent.displayName}</div>
          <div className={styles.agentCardEmailEnhanced}>{agent.email}</div>
        </div>
      </div>

      {/* Progress row: percentage + bar */}
      <div className={styles.agentCardProgressRow}>
        <span
          className={styles.agentCardProgressLabel}
          style={{ color: getProgressLabelColor(rate) }}
        >
          {rate.toFixed(1)}%
        </span>
        <div className={styles.agentCardProgressFull}>
          <div
            className={`${styles.progressBarFill} ${getBarFillClass(rate, styles as CSSModuleClasses)}`}
            style={{ width: `${Math.min(rate, 100)}%`, height: '100%' }}
          />
        </div>
      </div>

      {/* Badges: missing + expired */}
      {(agentStats.missingItems > 0 || agentStats.expiredItems > 0) && (
        <div className={styles.agentCardBadges}>
          {agentStats.missingItems > 0 && (
            <span className={`${styles['stat-badge']} ${styles['missing']}`}>
              {agentStats.missingItems} missing
            </span>
          )}
          {agentStats.expiredItems > 0 && (
            <span className={`${styles['stat-badge']} ${styles['expired']}`}>
              {agentStats.expiredItems} expired
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#9ca3af' }}>
            {agentStats.completedItems}/{agentStats.totalItems} done
          </span>
        </div>
      )}
    </button>
  );
};

export default AgentCompletionRow;

